/*
Origin Core 1.0

called from /snippets/scripts.liquid

DEVELOPER NOTE
--------------------
any changes to this file should be noted here, and also noted in the following wiki page:
https://unifiedco.atlassian.net/wiki/spaces/ORIGIN/pages/1413414932/Core+File+Changes+1.0

SUMMARY
--------------------
This script defines a custom web component <variant-picker> that manages variant selection for Shopify products. 
It listens for change events, updates the selected option, dispatches custom events, and fetches updated product or variant data via AJAX. 
The component morphs the DOM to reflect changes, updates the main content or just the variant picker as needed, and synchronizes the browser URL with the selected variant. 
It supports combined listings and ensures only the latest fetch request is processed. 
The component is registered if not already defined.

*/

import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;


  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (event.target.dataset.variantId) {
        url.searchParams.set('variant', event.target.dataset.variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    document.querySelector('[data-pdp-thumbs-vertical]')?.style.setProperty('opacity', '0');

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        //html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const { newProduct, autoSelected } = this.updateVariantPicker(html);

          document.querySelector('product-price').innerHTML = html.querySelector('product-price').innerHTML;

          try {
            const scope = this.closest('.shopify-section') || document;
            const currentBuyButtonsBlocks = scope.querySelectorAll('.buy-buttons-block');

            currentBuyButtonsBlocks.forEach((currentEl) => {
              // Blocks are uniquely targetable by the per-block class: buy-buttons-block--<block.id>
              const keyClass = Array.from(currentEl.classList).find((c) => c.startsWith('buy-buttons-block--'));
              if (!keyClass) return;

              const newEl = html.querySelector(`.${keyClass}`);
              if (!newEl) return;

              morph(currentEl, newEl);
            });
          } catch (e) {
            // Non-fatal: if anything goes wrong, variant updates should still work.
            console.warn('Buy buttons could not be updated on variant change', e);
          }

          try {
            const scope = this.closest('.shopify-section') || document;
            const currentBulletBlocks = scope.querySelectorAll('.bullet-points-block');

            currentBulletBlocks.forEach((currentEl) => {
              const keyClass = Array.from(currentEl.classList).find((c) => c.startsWith('bullet-points-block--'));
              if (!keyClass) return;

              const newEl = html.querySelector(`.${keyClass}`);
              if (!newEl) return;

              morph(currentEl, newEl);
            });
          } catch (e) {
            console.warn('Bullet points could not be updated on variant change', e);
          }

          try {
            const scope = this.closest('.shopify-section') || document;
            const currentCustomPaintBlocks = scope.querySelectorAll('.product-custom-paint-tub-foam-block');

            currentCustomPaintBlocks.forEach((currentEl) => {
              const keyClass = Array.from(currentEl.classList).find((c) =>
                c.startsWith('product-custom-paint-tub-foam-block--')
              );
              if (!keyClass) return;

              const newEl = html.querySelector(`.${keyClass}`);
              if (!newEl) return;

              morph(currentEl, newEl);
            });
          } catch (e) {
            console.warn('Custom paint / tub foam block could not be updated on variant change', e);
          }

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            const variant = JSON.parse(textContent);

            this.dispatchEvent(
              new VariantUpdateEvent(variant, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }

          // Re-render the media gallery only once all variant options are resolved.
          // If updateVariantPicker triggered an auto-select, another fetch is already
          // in flight — skip the gallery update here and let the final fetch handle it.
          if (!autoSelected) {
            try {
              const scope = this.closest('.shopify-section') || document;
              const galleryRoot = scope.querySelector('[rel="product_media_gallery"]');
              if (galleryRoot?.splide) {
                galleryRoot.splide.destroy(true);
                delete galleryRoot.splide;
              }
              if (galleryRoot) delete galleryRoot.dataset.pdpGalleryInit;

              const currentGallery = scope.querySelector('.pdp-media-gallery');
              const newGallery = html.querySelector('.pdp-media-gallery');
              if (currentGallery && newGallery) {
                morph(currentGallery, newGallery);
                if (window.Alpine && typeof Alpine.initTree === 'function') {
                  Alpine.initTree(currentGallery);
                }
              }
              document.dispatchEvent(new CustomEvent('pdp:gallery-reinit', { detail: { heroLast: true } }));
            } catch (e) {
              console.warn('Media gallery could not be updated on variant change', e);
            }
          }
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    // Capture the user's current selections before morphing. When an unavailable
    // combination is selected, Shopify falls back to rendering the first available
    // variant — after morph we re-apply what the user actually chose.
    const selectedValueIds = Array.from(
      this.querySelectorAll('fieldset input:checked, select option[selected]')
    ).map((el) => el.dataset.optionValueId).filter(Boolean);

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    // Re-apply the user's selections if morph reset them, but skip options that no variant has in
    // the new context — so selecting a new colour won't re-check a size that doesn't exist for that
    // colour. Sold-out values are re-applied, so a deliberate out-of-stock choice survives changing
    // another option and its stock message stays on screen.
    selectedValueIds.forEach((id) => {
      const el = this.querySelector(`[data-option-value-id="${id}"]`);
      if (!el || el.dataset.optionSelectable === 'false') return;
      if (el instanceof HTMLInputElement && !el.checked) el.checked = true;
      else if (el instanceof HTMLOptionElement && !el.selected) el.selected = true;
    });

    // For any option group left without a selection, auto-select the first available value
    // and dispatch a change event so the full variant update pipeline runs.
    let autoSelectedInput = null;

    this.querySelectorAll('fieldset').forEach((fieldset) => {
      if (fieldset.querySelector('input:checked')) return;
      const firstAvailable = fieldset.querySelector('input[data-option-available="true"]');
      if (firstAvailable instanceof HTMLInputElement) {
        firstAvailable.checked = true;
        autoSelectedInput = firstAvailable;
      }
    });

    this.querySelectorAll('select').forEach((select) => {
      const selectedOpt = select.options[select.selectedIndex];
      if (selectedOpt?.dataset.optionAvailable !== 'false') return;
      const firstAvailable = Array.from(select.options).find((o) => o.dataset.optionAvailable !== 'false');
      if (firstAvailable) {
        firstAvailable.selected = true;
        autoSelectedInput = select;
      }
    });

    if (autoSelectedInput) {
      autoSelectedInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    return { newProduct, autoSelected: !!autoSelectedInput };
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);

    // Re-initialize Alpine.js on the updated section
    if (window.Alpine && typeof Alpine.initTree === 'function') {
      Alpine.initTree(main);
    }
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

/**
 * Move the product media gallery to the selected variant's featured media.
 * @param {any} variant
 */
updateVariantImage(variant) {
  try {
    if (!variant || !variant.featured_media || !variant.featured_media.id) return;

    const mediaId = String(variant.featured_media.id);
    const scope = this.closest('.shopify-section') || document;

    // Slides should have data-media-id + data-slide-index
    const target = scope.querySelector(`[data-media-id="${mediaId}"]`);
    if (!target) return;

    const index = Number(target.dataset.slideIndex);
    const galleryRoot = scope.querySelector('[rel="product_media_gallery"]');

    if (galleryRoot && galleryRoot.splide && typeof galleryRoot.splide.go === 'function' && Number.isFinite(index)) {
      galleryRoot.splide.go(index);
      return;
    }

    if (typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  } catch (e) {
    console.warn('Could not update media gallery on variant change', e);
  }
}

}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
